import { Space, Typography, InputNumber, Card, Table, DatePicker } from 'antd'
import { useContext, useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBullseye, faChartPie, faPlus, faChartLine, faPercent } from '@fortawesome/pro-solid-svg-icons'
import BudgetCalculatorContext from './BudgetCalculatorContext'
import { formatDollars, formatDollarsAndCents, formatPercentage } from './budgetCalculatorModel'
import { estimateBreakevenDate } from './budgetModel'
import { CongratulationsModal } from './CongratulationsModal'
import BudgetDonutChart from './BudgetDonutChart'
import { buildBudgetDonutSlices } from './overviewModel'
import Confetti from 'react-confetti'
import dayjs from 'dayjs'

const { Title, Text, Paragraph } = Typography

const NewOverview = () => {
  const { summary, statistics, worksheet, showCongratulations, dismissCongratulations, dispatch } = useContext(BudgetCalculatorContext)
  const [showModal, setShowModal] = useState(false)
  const [showConfetti, setShowConfetti] = useState(false)
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  })

  const [hours, setHours] = useState<number>(100)
  const [materialsCost, setMaterialsCost] = useState<number>(10000)
  const desiredMargin = worksheet.data.profit.desiredNetProfitMargin * 100

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (showCongratulations) {
      setShowModal(true)
      setShowConfetti(true)

      const timer = setTimeout(() => {
        setShowConfetti(false)
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [showCongratulations])

  const handleCloseModal = () => {
    setShowModal(false)
    dismissCongratulations()
  }

  const calculateChargeAmount = () => {
    const laborCost = hours * summary.laborStatistics.manHourCost
    const laborOverhead = laborCost * statistics.laborMarkup
    const materialOverhead = materialsCost * worksheet.data.profit.overheadRecoveryMarkup.material
    const totalCost = laborCost + materialsCost + laborOverhead + materialOverhead
    const chargeAmount = totalCost / (1 - (desiredMargin / 100))
    return chargeAmount
  }

  const chargeAmount = calculateChargeAmount()
  const donutSlices = buildBudgetDonutSlices(summary, statistics)

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Calculator Section */}
      <div style={{
        backgroundColor: '#f5f5f5',
        padding: '40px',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <Title level={3} style={{ marginBottom: '32px' }}>
          <span style={{ fontWeight: 'normal' }}>If you had a job with </span>
          <InputNumber
            value={hours}
            onChange={(value) => setHours(value || 0)}
            min={0}
            style={{ width: '120px', margin: '0 8px', verticalAlign: 'middle' }}
            size="large"
          />
          <span style={{ fontWeight: 'normal' }}> hours and </span>
          <InputNumber
            prefix="$"
            value={materialsCost}
            onChange={(value) => setMaterialsCost(value || 0)}
            min={0}
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
            style={{ width: '150px', margin: '0 8px', verticalAlign: 'middle' }}
            size="large"
          />
        </Title>

        <Title level={3} style={{ margin: 0 }}>
          <span style={{ fontWeight: 'normal' }}>worth of materials, you would need to charge </span>
          <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
            {formatDollars(chargeAmount)}
          </span>
          <span style={{ fontWeight: 'normal' }}> to have a</span>
        </Title>

        <Title level={2} style={{ marginTop: '16px' }}>
          {formatPercentage(desiredMargin / 100)} profit margin
        </Title>
      </div>

      {/* Desired Profit Margin Card */}
      <Card>
        <Space direction="horizontal" style={{ width: '100%', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Space direction="vertical" size="small">
            <Space size="middle">
              <FontAwesomeIcon icon={faBullseye} style={{ color: '#ff6b4a', fontSize: '20px' }} />
              <Title level={4} style={{ margin: 0 }}>Desired Profit Margin</Title>
            </Space>
            <Paragraph style={{ color: '#666', margin: 0 }}>
              Adjust your desired your profit margin.
            </Paragraph>
          </Space>

          <Space direction="vertical" size="small" style={{ alignItems: 'flex-end' }}>
            <Text type="secondary">Desired Profit Margin</Text>
            <InputNumber
              value={desiredMargin}
              onChange={(value) => dispatch({ type: 'DESIRED_NET_PROFIT_MARGIN_CHANGED', payload: (value || 0) / 100 })}
              min={0}
              max={100}
              step={1}
              precision={2}
              addonAfter="%"
              style={{ width: '150px' }}
              size="large"
            />
          </Space>
        </Space>
      </Card>

      {/* Budget Summary Card */}
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space direction="vertical" size="small">
            <Space size="middle">
              <FontAwesomeIcon icon={faChartPie} style={{ color: '#ff6b4a', fontSize: '20px' }} />
              <Title level={4} style={{ margin: 0 }}>Budget Summary</Title>
            </Space>
            <Paragraph style={{ color: '#666', margin: 0 }}>
              Here's what you're projecting.
            </Paragraph>
          </Space>

          <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: '40px', alignItems: 'center' }}>
            {/* Donut Chart and Legend */}
            <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
              {/* Donut Chart */}
              <BudgetDonutChart
                data={donutSlices}
                centerText="Net Profit"
                centerValue={formatDollars(statistics.netProfit)}
              />
              {/* Legend */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}>
                {donutSlices.map((item) => (
                  <div key={item.label} style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: item.color,
                      marginRight: '8px'
                    }} />
                    <Text>{item.label}</Text>
                  </div>
                ))}
              </div>
            </div>

            {/* Metrics Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', rowGap: '32px' }}>
              {/* Revenue */}
              <Space direction="vertical" size={4}>
                <Text type="secondary">Revenue</Text>
                <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  {formatDollars(statistics.salesGoal)}
                </Text>
              </Space>

              {/* COGS */}
              <Space direction="vertical" size={4}>
                <Text type="secondary">COGS</Text>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                  <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    {formatDollars(statistics.totalCOGS)}
                  </Text>
                  <Text type="secondary">{formatPercentage(statistics.cogsPercentage)}</Text>
                </div>
              </Space>

              {/* Profit & Overhead Row - spans full width */}
              <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>
                {/* Gross Profit */}
                <Space direction="vertical" size={4}>
                  <Text type="secondary">Gross Profit</Text>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                    <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                      {formatDollars(statistics.grossProfit)}
                    </Text>
                    <Text type="secondary">{formatPercentage(statistics.grossProfitMargin)}</Text>
                  </div>
                </Space>

                {/* Overhead */}
                <Space direction="vertical" size={4}>
                  <Text type="secondary">Overhead</Text>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                    <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                      {formatDollars(statistics.totalOverhead)}
                    </Text>
                    <Text type="secondary">{formatPercentage(statistics.totalOverhead / statistics.salesGoal)}</Text>
                  </div>
                </Space>

                {/* Net Profit with donut chart */}
                <Space direction="vertical" size={4}>
                  <Text type="secondary">Net Profit</Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ position: 'relative', width: '60px', height: '60px' }}>
                      <svg width="60" height="60" viewBox="0 0 60 60">
                        <circle
                          cx="30"
                          cy="30"
                          r="24"
                          fill="none"
                          stroke="#e8e8e8"
                          strokeWidth="8"
                        />
                        <circle
                          cx="30"
                          cy="30"
                          r="24"
                          fill="none"
                          stroke={statistics.netProfitMargin >= 0 ? '#52c41a' : '#ff4d4f'}
                          strokeWidth="8"
                          strokeDasharray={2 * Math.PI * 24}
                          strokeDashoffset={2 * Math.PI * 24 * (1 - Math.abs(statistics.netProfitMargin))}
                          transform="rotate(-90 30 30)"
                        />
                      </svg>
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: statistics.netProfitMargin >= 0 ? '#52c41a' : '#ff4d4f'
                      }}>
                        {Math.round(statistics.netProfitMargin * 100)}%
                      </div>
                    </div>
                    <Text style={{ fontSize: '24px', fontWeight: 'bold', color: statistics.netProfitMargin >= 0 ? '#52c41a' : '#ff4d4f' }}>
                      {formatDollars(statistics.netProfit)}
                    </Text>
                  </div>
                </Space>
              </div>

              {/* Rates Row - spans full width */}
              <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>
                {/* Average Wage */}
                <Space direction="vertical" size={4}>
                  <Text type="secondary">Average Wage</Text>
                  <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    {formatDollarsAndCents(summary.laborStatistics.manHourCost)}
                  </Text>
                </Space>

                {/* Breakeven Rate */}
                <Space direction="vertical" size={4}>
                  <Text type="secondary">Breakeven Rate</Text>
                  <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    {formatDollarsAndCents(summary.laborStatistics.manHourCost * (1 + statistics.laborMarkup))}
                  </Text>
                </Space>

                {/* Labor Rate */}
                <Space direction="vertical" size={4}>
                  <Text type="secondary">Labor Rate</Text>
                  <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    {formatDollarsAndCents(statistics.manHourPrice)}
                  </Text>
                </Space>
              </div>
            </div>
          </div>
        </Space>
      </Card>

      {/* Labor Metrics Section */}
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space direction="vertical" size="small">
            <Title level={4} style={{ margin: 0 }}>
              <span style={{ color: '#ff6b4a' }}>#</span> Labor Metrics
            </Title>
            <Paragraph style={{ color: '#666', margin: 0 }}>
              Here are your labor numbers.
            </Paragraph>
          </Space>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '32px', rowGap: '24px' }}>
            {/* Man Hours */}
            <Space direction="vertical" size={4}>
              <Text type="secondary">Man Hours</Text>
              <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {statistics.totalManHours.toLocaleString()}
              </Text>
            </Space>

            {/* Billable Hours */}
            <Space direction="vertical" size={4}>
              <Text type="secondary">Billable Hours</Text>
              <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {statistics.totalBillableHours.toLocaleString()}
              </Text>
            </Space>

            {/* Billable Hours Percentage */}
            <Space direction="vertical" size={4}>
              <Text type="secondary">Billable Hours Percentage</Text>
              <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {formatPercentage(statistics.billableHoursPercentage)}
              </Text>
            </Space>

            {/* Revenue Per Hour */}
            <Space direction="vertical" size={4}>
              <Text type="secondary">Revenue Per Hour</Text>
              <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {formatDollars(statistics.revenuePerManHour)}
              </Text>
            </Space>

            {/* Gross Profit Per Hour */}
            <Space direction="vertical" size={4}>
              <Text type="secondary">Gross Profit Per Hour</Text>
              <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {formatDollars(statistics.grossProfitPerManHour)}
              </Text>
            </Space>

            {/* Net Profit Per Hour */}
            <Space direction="vertical" size={4}>
              <Text type="secondary">Net Profit Per Hour</Text>
              <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {formatDollars(statistics.netProfitPerManHour)}
              </Text>
            </Space>

            {/* Throughput Per Hour */}
            <Space direction="vertical" size={4}>
              <Text type="secondary">Throughput Per Hour</Text>
              <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {formatDollars(statistics.throughputPerManHour)}
              </Text>
            </Space>

            {/* Direct Labor Efficiency Ratio */}
            <Space direction="vertical" size={4}>
              <Text type="secondary">Direct Labor Efficiency Ratio</Text>
              <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {statistics.directLaborEfficiencyRatio.toFixed(2)}
              </Text>
            </Space>

            {/* Direct Labor Ratio */}
            <Space direction="vertical" size={4}>
              <Text type="secondary">Direct Labor Ratio</Text>
              <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {formatPercentage(statistics.directLaborRatio)}
              </Text>
            </Space>
          </div>
        </Space>
      </Card>

      {/* Breakeven Section */}
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space direction="vertical" size="small">
            <Space size="middle">
              <FontAwesomeIcon icon={faPlus} style={{ color: '#ff6b4a', fontSize: '20px' }} />
              <Title level={4} style={{ margin: 0 }}>Breakeven</Title>
            </Space>
            <Paragraph style={{ color: '#666', margin: 0 }}>
              Here's what needs to happen before you hit profitability.
            </Paragraph>
          </Space>

          <Table
            dataSource={[
              {
                key: '1',
                label: 'Breakeven Amount',
                value: formatDollars(statistics.breakevenAmount)
              },
              {
                key: '2',
                label: 'Revenue Per Day Needed to Reach Goal',
                value: formatDollars(statistics.dailyRevenueToHitSalesGoal)
              },
              {
                key: '3',
                label: 'Production days to break even',
                value: Math.round(statistics.productionDaysToBreakEven).toString()
              },
              {
                key: '4',
                label: 'Estimated Breakeven Date',
                value: dayjs(estimateBreakevenDate(worksheet.data.profit.season, statistics.productionDaysToBreakEven)).format('MMMM DD, YYYY')
              }
            ]}
            columns={[
              {
                dataIndex: 'label',
                key: 'label',
                width: '60%',
                render: (text) => <Text strong>{text}</Text>
              },
              {
                dataIndex: 'value',
                key: 'value',
                width: '40%',
                render: (text) => <Text>{text}</Text>
              }
            ]}
            pagination={false}
            showHeader={false}
            bordered={false}
          />
        </Space>
      </Card>

      {/* Company Comparison Section - Hidden for now */}
      {false && (
        <Card>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Space direction="vertical" size="small">
              <Space size="middle">
                <FontAwesomeIcon icon={faChartLine} style={{ color: '#ff6b4a', fontSize: '20px' }} />
                <Title level={4} style={{ margin: 0 }}>Your Company Compared to Others</Title>
              </Space>
              <Paragraph style={{ color: '#666', margin: 0 }}>
                Here's how your company stacks up against similar businesses.
              </Paragraph>
            </Space>

            <Table
              dataSource={[
                {
                  key: '1',
                  category: 'Labor',
                  percentage: formatPercentage(summary.labor / statistics.salesGoal),
                  benchmark: '40–45%',
                  insight: 'Within range',
                  color: '#9AE6B4'
                },
                {
                  key: '2',
                  category: 'Materials',
                  percentage: formatPercentage(summary.materials / statistics.salesGoal),
                  benchmark: '25–35%',
                  insight: 'Within range',
                  color: '#9AE6B4'
                },
                {
                  key: '3',
                  category: 'Overhead',
                  percentage: formatPercentage(statistics.totalOverhead / statistics.salesGoal),
                  benchmark: '15–20%',
                  insight: 'Consider optimizing fixed costs',
                  color: '#FBD38D'
                },
                {
                  key: '4',
                  category: 'Profit Target',
                  percentage: formatPercentage(statistics.netProfitMargin),
                  benchmark: '15–20%',
                  insight: 'Great job!',
                  color: '#9AE6B4'
                }
              ]}
              columns={[
                {
                  title: 'Category',
                  dataIndex: 'category',
                  key: 'category',
                  width: '25%',
                  render: (text) => <Text strong>{text}</Text>
                },
                {
                  title: '% of Revenue',
                  dataIndex: 'percentage',
                  key: 'percentage',
                  width: '20%',
                  render: (text, record) => (
                    <span style={{
                      backgroundColor: record.color,
                      padding: '4px 12px',
                      borderRadius: '4px',
                      display: 'inline-block',
                      fontWeight: 500
                    }}>
                      {text}
                    </span>
                  )
                },
                {
                  title: 'Benchmark',
                  dataIndex: 'benchmark',
                  key: 'benchmark',
                  width: '20%'
                },
                {
                  title: 'Insight',
                  dataIndex: 'insight',
                  key: 'insight',
                  width: '35%'
                }
              ]}
              pagination={false}
              bordered={true}
            />
          </Space>
        </Card>
      )}

      {/* Work Season Section */}
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space direction="vertical" size="small">
            <Space size="middle">
              <FontAwesomeIcon icon={faChartLine} style={{ color: '#ff6b4a', fontSize: '20px' }} />
              <Title level={4} style={{ margin: 0 }}>Work Season</Title>
            </Space>
            <Paragraph style={{ color: '#666', margin: 0 }}>
              Here's your working season and schedule.
            </Paragraph>
          </Space>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px' }}>
            {/* Start Date */}
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong>Start Date</Text>
              <DatePicker
                value={dayjs(worksheet.data.profit.season.startDate)}
                onChange={(date) => dispatch({
                  type: 'SEASON_START_DATE_CHANGED',
                  payload: date?.format('YYYY-MM-DD') ?? ''
                })}
                format="MMMM D, YYYY"
                style={{ width: '100%' }}
                size="large"
              />
            </Space>

            {/* End Date */}
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong>End Date</Text>
              <DatePicker
                value={dayjs(worksheet.data.profit.season.endDate)}
                onChange={(date) => dispatch({
                  type: 'SEASON_END_DATE_CHANGED',
                  payload: date?.format('YYYY-MM-DD') ?? ''
                })}
                format="MMMM D, YYYY"
                style={{ width: '100%' }}
                size="large"
              />
            </Space>

            {/* Working Days Per Week */}
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong>Working Days Per Week</Text>
              <InputNumber
                value={worksheet.data.profit.season.workingDaysPerWeek}
                onChange={(value) => dispatch({
                  type: 'SEASON_WORKING_DAYS_CHANGED',
                  payload: value ?? 5
                })}
                min={1}
                max={7}
                style={{ width: '100%' }}
                size="large"
              />
            </Space>

            {/* Production Days (read-only) */}
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong>Production Days</Text>
              <div style={{
                padding: '8px 12px',
                fontSize: '16px',
                fontWeight: 500,
                color: '#000',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                backgroundColor: '#f5f5f5',
                textAlign: 'center',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {statistics.productionDays}
              </div>
            </Space>
          </div>
        </Space>
      </Card>

      {/* Overhead Markup Percent Per Class Section */}
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space direction="vertical" size="small">
            <Space size="middle">
              <FontAwesomeIcon icon={faPercent} style={{ color: '#ff6b4a', fontSize: '20px' }} />
              <Title level={4} style={{ margin: 0 }}>Overhead Markup Percent Per Class</Title>
            </Space>
            <Paragraph style={{ color: '#666', margin: 0 }}>
              Set the overhead percentage you want applied to each class.
            </Paragraph>
          </Space>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px' }}>
            {/* Material */}
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong>Material</Text>
              <InputNumber
                value={worksheet.data.profit.overheadRecoveryMarkup.material * 100}
                onChange={(value) => dispatch({ type: 'MATERIAL_MARKUP_CHANGED', payload: (value ?? 0) / 100 })}
                addonAfter="%"
                min={0}
                style={{ width: '100%' }}
                size="large"
              />
            </Space>

            {/* Equipment */}
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong>Equipment</Text>
              <InputNumber
                value={worksheet.data.profit.overheadRecoveryMarkup.equipment * 100}
                onChange={(value) => dispatch({ type: 'EQUIPMENT_MARKUP_CHANGED', payload: (value ?? 0) / 100 })}
                addonAfter="%"
                min={0}
                style={{ width: '100%' }}
                size="large"
              />
            </Space>

            {/* Subcontractor */}
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong>Subcontractor</Text>
              <InputNumber
                value={worksheet.data.profit.overheadRecoveryMarkup.subcontractor * 100}
                onChange={(value) => dispatch({ type: 'SUBCONTRACTOR_MARKUP_CHANGED', payload: (value ?? 0) / 100 })}
                addonAfter="%"
                min={0}
                style={{ width: '100%' }}
                size="large"
              />
            </Space>

            {/* Labor (read-only) */}
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong>Labor</Text>
              <div style={{
                padding: '8px 12px',
                fontSize: '16px',
                fontWeight: 500,
                color: '#000',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                backgroundColor: '#f5f5f5',
                textAlign: 'center'
              }}>
                {formatPercentage(statistics.laborMarkup)}
              </div>
            </Space>
          </div>
        </Space>
      </Card>

      {/* Congratulations Modal */}
      <CongratulationsModal
        open={showModal}
        onClose={handleCloseModal}
        salesGoal={worksheet.data.profit.desiredSalesGoal}
        profitMargin={worksheet.data.profit.desiredNetProfitMargin}
        requiredSales={statistics.salesGoal}
      />

      {/* Confetti Effect - Full screen overlay */}
      {showConfetti && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 10000
        }}>
          <Confetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={800}
            gravity={0.3}
            initialVelocityY={20}
          />
        </div>
      )}
    </Space>
  )
}

export default NewOverview
